import { StringUtil, UrlUtil, Alignment } from "@mcleod/core";
import { LayoutProvidedReportDecoratorProps, LookupModelSearchEvent, Layout, ReportDecorator, SlideoutDecorator } from "@mcleod/components";
import { AutogenLayoutCustomerStatementsReport } from "./autogen/AutogenLayoutCustomerStatementsReport";
import { ModelCustomer } from "../models/ModelCustomer";

export class CustomerStatementsReport extends AutogenLayoutCustomerStatementsReport {
    customerId: string | null = null;
    customerIds: string[] = [];
    recipientInfoVisible: boolean = false;

    public override get reportDecoratorProps(): Partial<LayoutProvidedReportDecoratorProps> {
        return {
            reportPreviewTitle: "Customer Statements Report"
        };
    }

    onLoad(): void | Promise<void> {
        const customerId = UrlUtil.getSearchParamFromUrl("customer_id");
        if (!StringUtil.isEmptyString(customerId)){
            this.setupCustomerData(customerId);
            this.customerId = customerId;
            this.recipientInfoVisible = true;
            this.checkboxPayableContact.visible = true;
            this.textboxEmailRecipient.visible = true;
        }
    }

    private setupCustomerData(customerId: string){
        new ModelCustomer().searchSingle({ id: customerId }, "lme/ar/Customer").then(customerRow => {
            if (!customerRow) return;
            const description =  customerRow.get("name")
            this.sourceCustomerStatement.searchRow.setValueAndLookupModelData("customer_id", customerId, {
                "id": customerId, "name": description
                });
            this.excludabletextboxCustomer.displayData(this.sourceCustomerStatement.searchRow, null, 0);
        })
    }

    public setupMultipleCustomerData(customerIds: string[]) {
        if (!Array.isArray(customerIds) || customerIds.length === 0) {
            return;
        }

        this.customerIds = customerIds.map(id => id.trim()).filter(id => !StringUtil.isEmptyString(id));
        if (this.customerIds.length === 1) {
            this.setupCustomerData(this.customerIds[0]);
            this.customerId = this.customerIds[0];
            this.recipientInfoVisible = true;
            this.checkboxPayableContact.visible = true;
            this.textboxEmailRecipient.visible = true;
        } else if (this.customerIds.length > 1) {
            this.setupMultipleCustomersDisplayWithModelData();
        }
    }

    private async setupMultipleCustomersDisplayWithModelData() {
        try {
            // Fetch all customer data
            const customerPromises = this.customerIds.map(customerId =>
                new ModelCustomer().searchSingle({ id: customerId }, "lme/ar/Customer")
            );

            const customerRows = await Promise.all(customerPromises);
            const validCustomers = customerRows.filter(row => row != null);

            if (validCustomers.length > 0) {
                const lookupModelData = validCustomers.map(customerRow => ({
                    "id": customerRow.get("id"),
                    "name": customerRow.get("name")
                }));

                const customerIdValue = this.customerIds.join(",");
                this.sourceCustomerStatement.searchRow.setValueAndLookupModelData("customer_id", customerIdValue, lookupModelData);
                this.excludabletextboxCustomer.displayData(this.sourceCustomerStatement.searchRow, null, 0);

                // Set customerId to the first one for compatibility with existing recipient logic
                this.customerId = this.customerIds[0];
            }
        } catch (error) {
            console.error("Error setting up multiple customer data:", error);
            // Fallback to just setting the raw value
            const customerIdValue = this.customerIds.join(",");
            this.sourceCustomerStatement.searchRow.set("customer_id", customerIdValue);
            this.excludabletextboxCustomer.displayData(this.sourceCustomerStatement.searchRow, null, 0);
        }
    }

    CategoryBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.customer = "Y"
    }

    textboxRecipientBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        // For multiple customers, use the first customer ID for recipient lookup
        // This is a reasonable default since recipient lookup typically needs a single parent
        const parentRowId = this.customerIds.length > 0 ? this.customerIds[0] : this.customerId;
        event.filter.parent_row_id = parentRowId;
    }

    checkboxEmailStatementOnChange(){
        const hasCustomers = this.customerIds.length > 0 || !StringUtil.isEmptyString(this.customerId);
        if(this.checkboxEmailStatement.checked && (this.recipientInfoVisible || hasCustomers)){
            this.checkboxPayableContact.enabled = true;
            this.textboxEmailRecipient.enabled = true;
        }
        else {
            this.checkboxPayableContact.enabled = false;
            this.textboxEmailRecipient.enabled = false;
        }
    }

    public static showCustomerStatementsReportWithDecorator(customerIds: string[]): void {
        const customerStatementsReport = Layout.getLayout("lme/ar/reports/CustomerStatementsReport") as CustomerStatementsReport;
        customerStatementsReport.addLayoutLoadListener(() => {
            if (customerIds && customerIds.length > 0) {
                customerStatementsReport.setupMultipleCustomerData(customerIds);
            }
            customerStatementsReport.sourceCustomerStatement?.resetDefaultSearchRowToCurrent();
        });

        this.buildCustomerStatementsSlideoutTitle(customerIds).then(title => {
            this.showReportInSlideout(customerStatementsReport, title);
        });
    }

    private static showReportInSlideout(reportLayout: Layout, title: string) {
        const reportDecorator = new ReportDecorator({ layout: reportLayout });
        const layoutWrapper = new Layout({
            needsServerLayout: false,
            components: [ reportDecorator ],
            paddingTop: 12,
            paddingRight: 12,
            paddingBottom: 12
        });

        new SlideoutDecorator({
            layout: layoutWrapper,
            width: "95%",
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title,
            transitionOptions: {
                direction: Alignment.BOTTOM
            }
        });
    }

    private static async buildCustomerStatementsSlideoutTitle(customerIds: string[]): Promise<string> {
        if (!customerIds || customerIds.length === 0) {
            return "Customer Statements Report";
        }

        if (customerIds.length === 1) {
            try {
                const customerRow = await new ModelCustomer().searchSingle({ id: customerIds[0] }, "lme/ar/Customer");
                if (customerRow) {
                    const customerName = customerRow.get("name");
                    return `Customer Statements Report - ${customerName}`;
                }
            } catch (error) {
                console.error("Error fetching customer name for title:", error);
            }
            return `Customer Statements Report - Customer ${customerIds[0]}`;
        } else {
            return `Customer Statements Report - ${customerIds.length} Customers`;
        }
    }
}
